import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Plus, Plug } from "lucide-react";
import { ProviderIcon, CloudIcon } from "@/components/brand-icons";
import { PROVIDERS, ConnectorForm, ConnectorCard } from "@/components/git-connectors";
import { PLATFORMS, PlatformForm, PlatformCard, PlatformsEmpty } from "@/components/platform-connectors";
import { cn } from "@/lib/utils";

const SECTIONS = [
  { group: "git repositories", testid: "git", items: PROVIDERS.map((p) => ({ key: p.key, label: p.label, kind: "git" })) },
  { group: "platforms", testid: "platforms", items: PLATFORMS.map((p) => ({ key: p.key, label: p.label, kind: "platform" })) },
];

function ConnectorPane({ selected, onSelect, gitItems, platformItems }) {
  const count = (kind, key) => (kind === "git" ? gitItems : platformItems).filter((i) => (kind === "git" ? i.provider : i.platform) === key).length;
  return (
    <aside className="sticky top-0 flex h-screen w-[300px] min-w-[300px] max-w-[300px] shrink-0 flex-col self-start overflow-hidden border-r border-slate-200 bg-white" data-testid="connector-pane">
      <p className="flex h-16 items-center border-b border-slate-200 px-4 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// connectors</p>
      <div className="flex-1 overflow-y-auto p-2">
        {SECTIONS.map((s) => {
          const kind = s.items[0].kind;
          const allActive = selected.kind === kind && !selected.key;
          return (
            <div key={s.group} className="mb-4">
              <button type="button" onClick={() => onSelect({ kind, key: null })} data-testid={`pane-section-${s.testid}`} aria-pressed={allActive}
                className={cn("flex w-full items-center justify-between px-3 py-2 font-mono-tight text-[11px] uppercase tracking-widest transition-colors", allActive ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900")}>
                <span>// {s.group}</span>
                <span>{(kind === "git" ? gitItems : platformItems).length}</span>
              </button>
              <ul className="mt-1">
                {s.items.map((it) => {
                  const isActive = selected.kind === it.kind && selected.key === it.key;
                  return (
                    <li key={it.key}>
                      <button type="button" onClick={() => onSelect({ kind: it.kind, key: it.key })} data-testid={`pane-${it.kind}-${it.key}`} aria-pressed={isActive}
                        className={cn("flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors", isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}>
                        {it.kind === "git" ? <ProviderIcon provider={it.key} className="h-4 w-4 shrink-0" /> : <CloudIcon cloud={it.key} className="h-4 w-4 shrink-0" />}
                        <span className="min-w-0 flex-1 truncate font-display text-sm font-semibold">{it.label}</span>
                        <span className="font-mono-tight text-[11px]" data-testid={`pane-count-${it.kind}-${it.key}`}>{count(it.kind, it.key)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

const COPY = {
  git: { kicker: "git repositories", title: "Git repositories", blurb: "Link Git platforms so generated projects can be pushed straight into a repository.", add: "New Git connector", addTestid: "add-connector" },
  platform: { kicker: "platforms", title: "Platforms", blurb: "Register cloud credentials (service principals, access keys, service accounts) so Patrice AI can answer questions about your real estate — read-only.", add: "New platform", addTestid: "add-platform" },
};

function GitEmpty({ isAdmin }) {
  return (
    <div className="rounded-md border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="connectors-empty">
      <Plug className="mx-auto h-8 w-8 text-slate-300" />
      <h2 className="mt-4 font-display text-lg font-semibold">No connector yet</h2>
      <p className="mt-1 text-sm text-slate-500">
        {isAdmin ? "Add a GitHub, GitLab or Azure DevOps token to enable one-click pushes." : "Ask a workspace admin to add a Git connector."}
      </p>
      <div className="mt-6 flex justify-center gap-6 text-slate-400">
        {PROVIDERS.map((p) => (
          <span key={p.key} className="inline-flex items-center gap-2 text-xs"><ProviderIcon provider={p.key} className="h-4 w-4" /> {p.label}</span>
        ))}
      </div>
    </div>
  );
}

function GitSection({ items, visible, isAdmin, setItems, onEdit }) {
  if (items === null) return <p className="text-sm text-slate-500">Loading connectors…</p>;
  if (visible.length === 0) return <GitEmpty isAdmin={isAdmin} />;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="connectors-grid">
      {visible.map((c) => (
        <ConnectorCard key={c.id} c={c} isAdmin={isAdmin}
          onChanged={(n) => setItems((l) => l.map((x) => (x.id === n.id ? n : x)))}
          onDeleted={(id) => setItems((l) => l.filter((x) => x.id !== id))}
          onEdit={onEdit} />
      ))}
    </div>
  );
}

function PlatformSection({ items, visible, isAdmin, setItems, onEdit }) {
  if (items === null) return <p className="text-sm text-slate-500">Loading platforms…</p>;
  if (visible.length === 0) return <PlatformsEmpty isAdmin={isAdmin} />;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="platforms-grid">
      {visible.map((p) => (
        <PlatformCard key={p.id} p={p} isAdmin={isAdmin}
          onChanged={(n) => setItems((l) => l.map((x) => (x.id === n.id ? n : x)))}
          onDeleted={(id) => setItems((l) => l.filter((x) => x.id !== id))}
          onEdit={onEdit} />
      ))}
    </div>
  );
}

const upsert = (set) => (n) => set((l) => ((l || []).some((x) => x.id === n.id) ? l.map((x) => (x.id === n.id ? n : x)) : [...(l || []), n]));

export default function Connectors() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [platformsList, setPlatformsList] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState({ kind: "git", key: null });
  const [dialog, setDialog] = useState({ kind: null, editing: null });

  useEffect(() => {
    client.get("/connectors").then((r) => setItems(r.data));
    client.get("/platforms").then((r) => setPlatformsList(r.data));
    client.get("/platforms/providers").then((r) => setProviders(r.data));
  }, []);

  const isGit = selected.kind === "git";
  const copy = COPY[selected.kind];
  const visibleGit = (items || []).filter((c) => !selected.key || c.provider === selected.key);
  const visiblePlatforms = (platformsList || []).filter((p) => !selected.key || p.platform === selected.key);
  const openDialog = (kind, editing = null) => setDialog({ kind, editing });
  const closeDialog = (o) => { if (!o) setDialog({ kind: null, editing: null }); };

  return (
    <div className="grid grid-cols-[300px_minmax(0,1fr)]" data-testid="connectors-layout">
      <ConnectorPane selected={selected} onSelect={setSelected} gitItems={items || []} platformItems={platformsList || []} />
      <div className="min-w-0 p-10">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// connectors · {copy.kicker}</p>
            <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="page-title">{copy.title}</h1>
            <p className="mt-1 text-sm text-slate-600">{copy.blurb}</p>
          </div>
          {isAdmin && (
            <Button onClick={() => openDialog(selected.kind)} className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800" data-testid={copy.addTestid}>
              <Plus className="mr-2 h-4 w-4" /> {copy.add}
            </Button>
          )}
        </div>

        {isGit ? (
          <GitSection items={items} visible={visibleGit} isAdmin={isAdmin} setItems={setItems} onEdit={(c) => openDialog("git", c)} />
        ) : (
          <PlatformSection items={platformsList} visible={visiblePlatforms} isAdmin={isAdmin} setItems={setPlatformsList} onEdit={(p) => openDialog("platform", p)} />
        )}

        <ConnectorForm open={dialog.kind === "git"} editing={dialog.editing} onOpenChange={closeDialog} onCreated={upsert(setItems)} />
        <PlatformForm
          open={dialog.kind === "platform"}
          editing={dialog.editing}
          providers={providers}
          initialPlatform={!isGit && selected.key ? selected.key : "azure"}
          onOpenChange={closeDialog}
          onSaved={upsert(setPlatformsList)}
        />
      </div>
    </div>
  );
}
